import { z } from 'zod';
// Unified per-brand conversion funnel + deterministic leak attribution.
//
// The API-facing shape returned by buildBrandFunnel (apps/api/src/brand-funnel.ts).
// v1 is the ON-SITE funnel: landing → … → conversion, computed from ClickHouse
// via windowFunnel over a campaign's ordered conversion-event steps. The paid
// top-of-funnel (impression/click) and the cross-silo stitch land later, once
// the instrumentation prerequisites ship — see the funnel brain roadmap (CommitMind).
//
// The ClickHouse-side compute types (FunnelStepResult / FunnelResult) live in
// @magig/clickhouse; this is the enriched, founder-facing model.
/** Which silo a step came from. v1 is all 'analytics' (on-site); 'paid'
 *  steps (impression/click) arrive with the cross-silo stitch. */
export const brandFunnelStepSourceSchema = z.enum(['paid', 'analytics']);
export const brandFunnelStepSchema = z.object({
    /** Step label — a conversion-event name, or 'impression'/'click' for paid. */
    name: z.string(),
    source: brandFunnelStepSourceSchema,
    /** Distinct visitors who reached at least this step. */
    visitors: z.number().int().min(0),
    /** visitors[i] / visitors[i-1], 0..1. 1 at the top. */
    rateFromPrev: z.number().min(0).max(1),
    /** visitors[i] / visitors[0], 0..1. */
    rateFromTop: z.number().min(0).max(1),
    /** 1 - rateFromPrev, 0..1. The leak size entering this step. 0 at top. */
    dropOffPct: z.number().min(0).max(1),
    /** Same step's drop-off over the prior window, for delta comparison.
     *  null when there's no baseline (cold start) or for paid steps. */
    baselineDropOffPct: z.number().min(0).max(1).nullable(),
});
export const brandFunnelLeakSchema = z.object({
    stepName: z.string(),
    stepIndex: z.number().int().min(0),
    dropOffPct: z.number().min(0).max(1),
    /** current dropOff − baseline dropOff. Positive = worsening. */
    deltaVsBaseline: z.number().min(-1).max(1),
    /** True when the leak grew meaningfully vs the prior window (a *new*
     *  leak, not a structural one). */
    isNewLeak: z.boolean(),
    /** Statistical confidence in the leak, from the Wilson lower bound on the
     *  drop rate — so small brands aren't silenced but small samples aren't
     *  overstated. 'confident' = even pessimistically a big drop; 'tentative' =
     *  a stark drop but a small sample (Mag hedges). */
    confidence: z.enum(['confident', 'tentative']),
    /** The denominator behind the leak — visitors who reached the step BEFORE
     *  the drop. Lets the UI/agent show "8 of 9" honestly. */
    sampleSize: z.number().int().min(0),
    /** Deterministic, Mag-voice one-liner — computed once, then read by the
     *  agent + the site auditor so the leak read never diverges across surfaces.
     *  Already carries the hedge when confidence is 'tentative'. */
    summary: z.string().min(1).max(280),
});
export const brandFunnelSchema = z.object({
    brandId: z.string().uuid(),
    /** null = brand rollup; otherwise the campaign whose spine + traffic this is. */
    campaignId: z.string().uuid().nullable(),
    steps: z.array(brandFunnelStepSchema),
    /** The biggest actionable leak. null when data is too thin to call one. */
    biggestLeak: brandFunnelLeakSchema.nullable(),
    scannedRange: z.object({ from: z.string(), to: z.string() }),
    /** windowFunnel completion window used, in seconds. */
    funnelWindowSeconds: z.number().int().positive(),
    /** Below the noise floor — visitor counts too low to trust. Suppresses leak. */
    thin: z.boolean(),
    /** Total paid spend (Meta + Google) over the window IN USD — only
     *  meaningful for a per-campaign funnel with a paid top; 0 for
     *  on-site-only / rollup.
     *
     *  NULLABLE, and the null is the point: it means the window's spend
     *  cannot be stated in dollars, because the accounts bill in something
     *  else, or mix currencies, or include rows whose currency was never
     *  captured. This field used to be a plain number that quietly held
     *  soles for a Peruvian advertiser. A client must render spendNative
     *  with spendCurrency in that case, or render nothing — never this. */
    spendUsd: z.number().min(0).nullable(),
    /** The same total in whatever the accounts actually bill in. Only money
     *  when read together with spendCurrency. */
    spendNative: z.number().min(0),
    /** ISO 4217 for spendNative, or null when the window has no single
     *  currency to name. */
    spendCurrency: z
        .string()
        .regex(/^[A-Z]{3}$/)
        .nullable(),
    /** True when the click→landing seam is unreliable — landing visitors exceed
     *  paid clicks (organic traffic + inherent client-side tracking loss). The
     *  UI should caveat that one step rather than read it as a clean rate. */
    attributionGap: z.boolean(),
});
//# sourceMappingURL=brand-funnel.js.map